import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, tap, timeout } from 'rxjs';

import type { IOnboardingStatusResponse } from '../../../auth/interfaces';
import { OnboardingApiService } from '../../shared/services/onboarding-api.service';
import {
  SPONSOR_ONBOARDING_DEFAULT_COUNTRY,
  SPONSOR_ONBOARDING_STATUS_LOAD_TIMEOUT_MS,
} from '../constants/sponsor-onboarding.constants';
import type {
  ISponsorOnboardingApiDocument,
  ISponsorOnboardingApiGeography,
} from '../models/sponsor-onboarding-api-document.interface';
import type { ISponsorAudienceStepData } from '../models/sponsor-audience-step-data.interface';
import type { ISponsorCampaignBudgetStepData } from '../models/sponsor-campaign-budget-step-data.interface';
import type { ISponsorCompanyActionsStepData } from '../models/sponsor-company-actions-step-data.interface';
import type { ISponsorGeographyStepData } from '../models/sponsor-geography-step-data.interface';
import type { ISponsorGoalsStepData } from '../models/sponsor-goals-step-data.interface';
import type { ISponsorOnboardingDraft } from '../models/sponsor-onboarding-draft.interface';
import type { ISponsorPreferencesStepData } from '../models/sponsor-preferences-step-data.interface';
import type { ISponsorPrimaryLocationApi } from '../models/sponsor-primary-location-api.interface';
import type { ISponsorPrimaryLocationDraft } from '../models/sponsor-primary-location-draft.interface';
import type { ISponsorSectorStepData } from '../models/sponsor-sector-step-data.interface';
import { SponsorOnboardingDraftService } from './sponsor-onboarding-draft.service';

@Injectable({
  providedIn: 'root',
})
export class SponsorOnboardingHydrateService {
  private readonly onboardingApi = inject(OnboardingApiService);
  private readonly draftService = inject(SponsorOnboardingDraftService);

  private readonly _statusLoaded = signal<boolean>(false);
  private readonly _statusHydrationFailed = signal<boolean>(false);
  private readonly _statusCompleted = signal<boolean>(false);
  private readonly _statusCurrentStep = signal<number>(1);

  private statusLoadRequest: Observable<boolean> | null = null;

  public readonly statusLoaded = this._statusLoaded.asReadonly();
  public readonly statusHydrationFailed = this._statusHydrationFailed.asReadonly();
  public readonly statusCompleted = this._statusCompleted.asReadonly();
  public readonly statusCurrentStep = this._statusCurrentStep.asReadonly();

  public retryStatusHydration(): Observable<boolean> {
    this._statusLoaded.set(false);
    this._statusHydrationFailed.set(false);
    this.statusLoadRequest = null;
    return this.ensureStatusLoaded();
  }

  public markStatusHydrationFailed(): void {
    this._statusHydrationFailed.set(true);
    this._statusLoaded.set(true);
    this.statusLoadRequest = null;
  }

  public refreshStatus(): Observable<boolean> {
    return this.ensureStatusLoaded({ forceRefresh: true });
  }

  public hydrateFromStatus(options?: { forceRefresh?: boolean }): Observable<ISponsorOnboardingDraft> {
    return this.prepareStepFromStatus(options);
  }

  public prepareStepFromStatus(options?: { forceRefresh?: boolean }): Observable<ISponsorOnboardingDraft> {
    return this.ensureStatusLoaded({ forceRefresh: options?.forceRefresh ?? false }).pipe(
      map(() => this.draftService.draft()),
    );
  }

  public ensureStatusLoaded(options?: { forceRefresh?: boolean }): Observable<boolean> {
    if (!options?.forceRefresh && this._statusLoaded()) {
      return of(this._statusCompleted());
    }
    if (options?.forceRefresh) {
      this.statusLoadRequest = null;
    }
    if (!this.statusLoadRequest) {
      this.statusLoadRequest = this.createStatusLoadRequest();
    }
    return this.statusLoadRequest;
  }

  public getOnboardingState(): Observable<IOnboardingStatusResponse> {
    return this.onboardingApi.getStatus().pipe(tap((response) => this.applyStatusResponse(response)));
  }

  public resetStatusSession(): void {
    this._statusLoaded.set(false);
    this._statusHydrationFailed.set(false);
    this._statusCompleted.set(false);
    this._statusCurrentStep.set(1);
    this.statusLoadRequest = null;
  }

  public markCompletedAfterFinish(): void {
    this._statusCompleted.set(true);
    this._statusLoaded.set(true);
    this._statusHydrationFailed.set(false);
    this.statusLoadRequest = null;
  }

  public applyStatusResponse(response: IOnboardingStatusResponse): void {
    this._statusLoaded.set(true);
    if (!response.success || !response.data) {
      this._statusHydrationFailed.set(true);
      return;
    }
    const data = response.data;
    this._statusCompleted.set(data.completed === true);
    this._statusCurrentStep.set(data.current_step ?? 1);
    if (data.completed) {
      return;
    }
    const sponsorUnknown = data.onboarding_data?.sponsor;
    if (sponsorUnknown === null) {
      this.draftService.setDraft(this.draftService.buildDefaultDraft());
      return;
    }
    if (sponsorUnknown === undefined) {
      return;
    }
    if (typeof sponsorUnknown === 'object') {
      this.hydrateFromSponsorDocument(sponsorUnknown as ISponsorOnboardingApiDocument);
    }
  }

  public createStatusLoadRequest(): Observable<boolean> {
    return this.onboardingApi.getStatus().pipe(
      timeout(SPONSOR_ONBOARDING_STATUS_LOAD_TIMEOUT_MS),
      tap((response) => {
        this._statusHydrationFailed.set(false);
        this.applyStatusResponse(response);
      }),
      map((response) => Boolean(response.success && response.data?.completed === true)),
      catchError(() => {
        this._statusHydrationFailed.set(true);
        this._statusLoaded.set(true);
        return of(false);
      }),
      finalize(() => {
        this.statusLoadRequest = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  public hydrateFromSponsorDocument(document: ISponsorOnboardingApiDocument): void {
    const fallback = this.draftService.draft();
    const geographyApi = this.resolveApiGeography(document);
    const merged: ISponsorOnboardingDraft = {
      goals: this.mapGoalsFromDocument(document, fallback.goals),
      audience: this.mapAudienceFromDocument(document, fallback.audience),
      geography: this.mapGeographyFromDocument(document, geographyApi, fallback.geography),
      sector: this.mapSectorFromDocument(document, fallback.sector),
      campaign_budget: this.mapCampaignBudgetFromDocument(document, fallback.campaign_budget),
      company_actions: this.mapCompanyActionsFromDocument(document, fallback.company_actions),
      preferences: this.mapPreferencesFromDocument(document, fallback.preferences),
    };
    if (!merged.geography.primary_location.country.trim()) {
      merged.geography.primary_location.country = SPONSOR_ONBOARDING_DEFAULT_COUNTRY;
    }
    this.draftService.setDraft(merged);
  }

  public resolveApiGeography(document: ISponsorOnboardingApiDocument): ISponsorOnboardingApiGeography {
    const nested = document.geography;
    return {
      geographic_radius: nested?.geographic_radius ?? document.geographic_radius ?? null,
      primary_location: nested?.primary_location ?? document.primary_location ?? null,
    };
  }

  public mapApiPrimaryLocationToDraft(
    apiLocation: ISponsorPrimaryLocationApi,
    fallback: ISponsorPrimaryLocationDraft,
  ): ISponsorPrimaryLocationDraft {
    return {
      formatted_address: apiLocation.formatted_address ?? fallback.formatted_address,
      address: apiLocation.address?.trim() ?? fallback.address,
      city: apiLocation.city?.trim() ?? fallback.city,
      province: apiLocation.province?.trim() ?? fallback.province,
      region: apiLocation.region ?? fallback.region,
      postal_code: apiLocation.postal_code ?? fallback.postal_code,
      country: apiLocation.country?.trim() || SPONSOR_ONBOARDING_DEFAULT_COUNTRY,
      latitude: apiLocation.latitude ?? fallback.latitude,
      longitude: apiLocation.longitude ?? fallback.longitude,
    };
  }

  private mapGoalsFromDocument(
    document: ISponsorOnboardingApiDocument,
    fallback: ISponsorGoalsStepData,
  ): ISponsorGoalsStepData {
    const goalsApi = document.goals;
    const primaryGoal =
      goalsApi?.primary_goal ?? document.primary_goal ?? fallback.primary_goal;
    const secondaryGoal =
      goalsApi?.secondary_goal ?? document.secondary_goal ?? fallback.secondary_goal;
    const companyName =
      this.draftService.toNullableTrimmedString(
        goalsApi?.company_name ?? document.company_name,
      ) ?? fallback.company_name;
    return {
      company_name: companyName,
      primary_goal: primaryGoal,
      secondary_goal:
        secondaryGoal !== null && secondaryGoal === primaryGoal ? null : secondaryGoal,
    };
  }

  private mapAudienceFromDocument(
    document: ISponsorOnboardingApiDocument,
    fallback: ISponsorAudienceStepData,
  ): ISponsorAudienceStepData {
    const audienceApi = document.audience;
    return {
      primary_audience:
        audienceApi?.primary_audience ??
        document.primary_audience ??
        fallback.primary_audience,
      secondary_audience:
        audienceApi?.secondary_audience ??
        document.secondary_audience ??
        fallback.secondary_audience,
    };
  }

  private mapGeographyFromDocument(
    document: ISponsorOnboardingApiDocument,
    geographyApi: ISponsorOnboardingApiGeography,
    fallback: ISponsorGeographyStepData,
  ): ISponsorGeographyStepData {
    return {
      geographic_radius:
        geographyApi.geographic_radius ??
        document.geographic_radius ??
        fallback.geographic_radius,
      primary_location: geographyApi.primary_location
        ? this.mapApiPrimaryLocationToDraft(
            geographyApi.primary_location,
            fallback.primary_location,
          )
        : fallback.primary_location,
    };
  }

  private mapSectorFromDocument(
    document: ISponsorOnboardingApiDocument,
    fallback: ISponsorSectorStepData,
  ): ISponsorSectorStepData {
    return {
      sector: document.sector ?? fallback.sector,
      sector_other: document.sector_other ?? null,
    };
  }

  private mapCampaignBudgetFromDocument(
    document: ISponsorOnboardingApiDocument,
    fallback: ISponsorCampaignBudgetStepData,
  ): ISponsorCampaignBudgetStepData {
    const campaignBudgetApi = document.campaign_budget;
    return {
      budget_range:
        campaignBudgetApi?.budget_range ??
        document.budget_range ??
        fallback.budget_range,
      campaign_duration:
        campaignBudgetApi?.campaign_duration ??
        document.campaign_duration ??
        fallback.campaign_duration,
    };
  }

  private mapCompanyActionsFromDocument(
    document: ISponsorOnboardingApiDocument,
    fallback: ISponsorCompanyActionsStepData,
  ): ISponsorCompanyActionsStepData {
    return {
      company_actions: this.draftService.normalizeCompanyActions(
        document.company_actions ?? fallback.company_actions,
      ),
    };
  }

  private mapPreferencesFromDocument(
    document: ISponsorOnboardingApiDocument,
    fallback: ISponsorPreferencesStepData,
  ): ISponsorPreferencesStepData {
    const preferencesApi = document.preferences;
    return {
      sports_interest: this.draftService.normalizeSportsInterest(
        preferencesApi?.sports_interest ?? document.sports_interest ?? fallback.sports_interest,
      ),
      avoid_preferences: this.draftService.normalizeAvoidPreferences(
        preferencesApi?.avoid_preferences ??
          document.avoid_preferences ??
          fallback.avoid_preferences,
      ),
    };
  }
}
